import { Box, Circle, Flex, Grid, HStack, SimpleGrid, Skeleton, SkeletonCircle, Table, Tbody, Td, Th, Thead, Tr, VStack } from "@chakra-ui/react";

export const shimmerAnimation = `
  @keyframes shimmer {
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  }
`;

export const StatCardSkeleton = () => (
  <Box bg="white" rounded="lg" shadow="sm" p={4}>
    <Flex justify="space-between" mb={3}>
      <SkeletonCircle size="12" />
      <Box textAlign="right">
        <Skeleton height="20px" width="60px" mb={2} />
        <Skeleton height="12px" width="80px" />
      </Box>
    </Flex>
    <Skeleton height="14px" width="120px" mb={2} />
    <Flex justify="space-between" align="flex-end">
      <Skeleton height="32px" width="80px" />
      <Flex align="flex-end" gap={0.5} h="32px">
        {[...Array(7)].map((_, i) => (
          <Skeleton key={i} width="6px" height={`${Math.random() * 100}%`} minH="4px" />
        ))}
      </Flex>
    </Flex>
  </Box>
);

export const ChartSkeleton = () => (
  <Box bg="white" rounded="lg" shadow="sm" p={6}>
    <Skeleton height="24px" width="200px" mb={6} />
    <Grid templateColumns="repeat(4, 1fr)" gap={4} mb={6}>
      {[...Array(4)].map((_, idx) => (
        <Box key={idx} bg="gray.50" rounded="lg" p={4} textAlign="center">
          <Skeleton height="14px" width="80px" mx="auto" mb={2} />
          <Skeleton height="32px" width="60px" mx="auto" />
        </Box>
      ))}
    </Grid>
    <Skeleton height="200px" width="100%" />
  </Box>
);

export const CalendarSkeleton = () => (
  <Box bg="white" rounded="lg" shadow="sm" p={5} h="100%">
    <Skeleton height="24px" width="180px" mb={4} />
    <Box mb={4}>
      <Flex justify="space-between" align="center" mb={3}>
        <Skeleton height="32px" width="32px" rounded="md" />
        <Skeleton height="20px" width="120px" />
        <Skeleton height="32px" width="32px" rounded="md" />
      </Flex>
      <SimpleGrid columns={7} spacing={1} mb={2}>
        {[...Array(7)].map((_, idx) => (
          <Skeleton key={idx} height="20px" width="100%" />
        ))}
      </SimpleGrid>
      <SimpleGrid columns={7} spacing={1}>
        {[...Array(35)].map((_, idx) => (
          <Skeleton key={idx} height="32px" width="100%" rounded="md" />
        ))}
      </SimpleGrid>
    </Box>
    <VStack spacing={2} align="stretch">
      {[...Array(4)].map((_, idx) => (
        <Flex key={idx} align="center" gap={3} p={3} bg="gray.50" rounded="lg">
          <SkeletonCircle size="10" />
          <Box flex={1}>
            <Skeleton height="16px" width="120px" mb={1} />
            <Skeleton height="12px" width="80px" mb={1} />
            <Skeleton height="12px" width="80px" />
          </Box>
        </Flex>
      ))}
    </VStack>
  </Box>
);

export const ComparisonSkeleton = () => (
  <Box bg="white" rounded="lg" shadow="sm" p={6}>
    <Skeleton height="24px" width="300px" mb={4} />
    <Grid templateColumns="repeat(2, 1fr)" gap={4} mb={4}>
      {[...Array(2)].map((_, idx) => (
        <Box key={idx} bg="gray.50" rounded="lg" p={4} textAlign="center">
          <Skeleton height="14px" width="140px" mx="auto" mb={2} />
          <Skeleton height="32px" width="60px" mx="auto" />
        </Box>
      ))}
    </Grid>
    <Skeleton height="64px" width="100%" rounded="lg" />
  </Box>
);

export const TableSkeleton = () => (
  <Box bg="white" rounded="lg" shadow="sm" overflow="hidden">
    <Flex p={6} borderBottom="1px" borderColor="gray.200" justify="space-between" align="center">
      <Skeleton height="24px" width="150px" />
      <Skeleton height="32px" width="80px" />
    </Flex>
    <Box overflowX="auto">
      <Table variant="simple">
        <Thead bg="gray.50">
          <Tr>
            <Th><Skeleton height="16px" width="60px" /></Th>
            <Th><Skeleton height="16px" width="100px" /></Th>
            <Th><Skeleton height="16px" width="60px" /></Th>
            <Th><Skeleton height="16px" width="60px" /></Th>
          </Tr>
        </Thead>
        <Tbody>
          {[...Array(5)].map((_, index) => (
            <Tr key={index}>
              <Td>
                <HStack spacing={3}>
                  <SkeletonCircle size="10" />
                  <Box>
                    <Skeleton height="16px" width="120px" mb={1} />
                    <Skeleton height="12px" width="100px" />
                  </Box>
                </HStack>
              </Td>
              <Td><Skeleton height="16px" width="140px" /></Td>
              <Td><Skeleton height="16px" width="100px" /></Td>
              <Td><Skeleton height="24px" width="80px" /></Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  </Box>
);

export const PopularDoctorSkeleton = () => (
  <Box bg="white" rounded="lg" shadow="sm" p={6}>
    <Skeleton height="24px" width="200px" mb={6} />
    <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={4}>
      {[1, 2, 3].map((i) => (
        <Box key={i} p={4} bg="gray.50" rounded="lg">
          <Flex gap={3} align="center" mb={3}>
            <Circle size="40px" className="shimmer" />
            <Box flex={1}>
              <Box h="20px" w="70%" className="shimmer" rounded="md" mb={2} />
              <Box h="20px" w="50%" className="shimmer" rounded="md" />
              <Box h="20px" w="50%" className="shimmer" rounded="md" />
            </Box>
          </Flex>
          <Box h="20px" w="60%" className="shimmer" rounded="md" />
        </Box>
      ))}
    </Grid>
  </Box>
);

export const TopChartSkeleton = () => (
  <Box bg="white" rounded="lg" shadow="sm" p={6}>
    <Flex justify="space-between" align="center" mb={5}>
      <Box h="20px" w="120px" className="shimmer" rounded="md" />
    </Flex>
    <VStack spacing={3} align="stretch">
      {[1, 2, 3, 4].map((j) => (
        <Flex key={j} align="center" gap={3} p={3} bg="gray.50" rounded="lg">
          <Circle size="40px" className="shimmer" />
          <Box flex={1}>
            <Box h="14px" w="70%" className="shimmer" rounded="md" mb={2} />
            <Box h="12px" w="50%" className="shimmer" rounded="md" />
          </Box>
        </Flex>
      ))}
    </VStack>
  </Box>
);